// A link to one item (an action, a date, a decision or email card) lands on it: once the
// page's data has loaded, scroll the element with the URL's hash id into view and flash
// it, so the reader sees which item they were sent to, and open it in its table.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// A hand-typed or truncated hash must not throw.
export const hashId = (hash: string): string => {
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
};

const LANDED_CLASS = "hash-landed";
const LANDED_MS = 2600;
// The element may render a frame or two after the data arrives.
const ATTEMPTS = 20;

export const useScrollToHash = (ready: boolean) => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !hash) return;
    const id = hashId(hash);
    let tries = 0;
    let timer: number | undefined;
    let frame = 0;
    const land = () => {
      const target = document.getElementById(id);
      if (!target) {
        if (tries++ < ATTEMPTS) frame = window.requestAnimationFrame(land);
        return;
      }
      // An item inside a collapsed section is opened to, not scrolled to unseen.
      for (let el = target.parentElement; el; el = el.parentElement) {
        if (el instanceof HTMLDetailsElement) el.open = true;
      }
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      target.classList.add(LANDED_CLASS);
      timer = window.setTimeout(
        () => target.classList.remove(LANDED_CLASS),
        LANDED_MS,
      );
    };
    land();
    return () => {
      window.cancelAnimationFrame(frame);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [ready, hash]);
};

/** The rows a table has open, starting with the one a link landed on. */
export const useHashOpen = () => {
  const { hash } = useLocation();
  const landedId = hashId(hash);
  const [openIds, setOpenIds] = useState<Set<string>>(
    () => new Set(landedId ? [landedId] : []),
  );
  useEffect(() => {
    if (landedId) setOpenIds((ids) => new Set(ids).add(landedId));
  }, [landedId]);
  const toggle = (id: string) =>
    setOpenIds((ids) => {
      const next = new Set(ids);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const close = (id: string) =>
    setOpenIds((ids) => {
      if (!ids.has(id)) return ids;
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  return { landedId, openIds, toggle, close };
};
